/**
 * RateLimitsInfo.tsx
 * Display current tenant's rate limits (for regular users)
 * Shows what limits apply to them based on their plan
 */

import React, { useEffect, useState } from 'react';
import api from '../../shared/api/apiClient';
import { useTranslation } from 'react-i18next';
import './RateLimitsInfo.css';

export function RateLimitsInfo() {
  const { t } = useTranslation();
  const [limits, setLimits] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchLimits();
  }, []);

  const fetchLimits = async () => {
    try {
      const response = await api.get('/api/system/rate-limits/current');
      setLimits(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching rate limits:', err);
      setError(
        err?.response?.data?.detail ||
          t('error_fetching_rate_limits', {
            defaultValue:
              'Failed to load rate limits. Please check your session and try again.',
          })
      );
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="rate-limits-loading">
        {t('loading', { defaultValue: 'Loading' })}...
      </div>
    );
  }

  if (error) {
    return <div className="rate-limits-error">{error}</div>;
  }

  if (!limits) {
    return null;
  }

  const limitsList = [
    {
      key: 'email_send',
      label: t('rate_limit_email_sending', { defaultValue: 'Email Sending' }),
      icon: <i className="lni lni-envelope-1"></i>,
    },
    {
      key: 'telephony_call',
      label: t('rate_limit_phone_calls', { defaultValue: 'Phone Calls' }),
      icon: <i className="lni lni-telephone-3"></i>,
    },
    {
      key: 'ai_summary',
      label: t('rate_limit_ai_analysis', { defaultValue: 'AI Analysis' }),
      icon: <i className="lni lni-text-format"></i>,
    },
    {
      key: 'chat_message',
      label: t('rate_limit_chat_messages', { defaultValue: 'Chat Messages' }),
      icon: <i className="lni lni-wechat"></i>,
    },
    {
      key: 'reports_export',
      label: t('rate_limit_report_export', { defaultValue: 'Report Export' }),
      icon: <i className="lni lni-bar-chart-4"></i>,
    },
    {
      key: 'general',
      label: t('rate_limit_general_api', { defaultValue: 'General API' }),
      icon: <i className="lni lni-plug-1"></i>,
    },
  ];

  return (
    <div className="rate-limits-info-container">
      <div className="rate-limits-header">
        <h3>{t('rate_limits_title', { defaultValue: 'Your Rate Limits' })}</h3>
        <span className="plan-badge">{limits.plan?.toUpperCase()}</span>
      </div>

      {!limits.rate_limiting_enabled && (
        <div className="rate-limits-disabled-warning">
          ⚠️{' '}
          {t('rate_limits_disabled_warning', {
            defaultValue: 'Rate limiting is disabled for your account',
          })}
        </div>
      )}

      <div className="rate-limits-grid">
        {limitsList.map((item) => {
          const limit = limits.limits?.[item.key] || t('n_a', { defaultValue: 'N/A' });

          return (
            <div key={item.key} className="rate-limit-card">
              <div className="limit-icon">{item.icon}</div>
              <div className="limit-name">{item.label}</div>
              <div className="limit-value">
                <code>{limit}</code>
              </div>
            </div>
          );
        })}
      </div>

      <div className="rate-limits-info-text">
        <p>
          🔒{' '}
          {t('rate_limits_info_protection', {
            defaultValue:
              'These limits protect our service from abuse and ensure fair usage for all users.',
          })}
        </p>
        <p>
          ⚡{' '}
          {t('rate_limits_info_retry', {
            defaultValue:
              "When you exceed a limit, you'll receive a 429 Too Many Requests error with a Retry-After header telling you when to try again.",
          })}
        </p>
      </div>
    </div>
  );
}

export default RateLimitsInfo;